import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const BlogContext = createContext();

export const useBlogs = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlogs must be used within a BlogProvider');
  }
  return context;
};

const API_URL = 'https://airexheater.in/api';

export const BlogProvider = ({ children }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, canEdit, canDelete } = useAuth();

  // Fetch blogs from API on mount
  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      // For dashboard, use admin endpoint with auth
      const endpoint = token ? `${API_URL}/admin/blogs` : `${API_URL}/blogs`;
      const options = token ? {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      } : {};
      
      const response = await fetch(endpoint, options);
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || data);
      } else {
        console.error('Failed to fetch blogs');
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBlog = async (blogData) => {
    if (!canEdit()) {
      return { success: false, error: 'You do not have permission to add blogs' };
    }
    
    try {
      const response = await fetch(`${API_URL}/admin/blogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(blogData),
      });
      
      const data = await response.json();
      if (response.ok) {
        setBlogs(prev => [data.blog, ...prev]);
        return { success: true, blog: data.blog };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error adding blog:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const updateBlog = async (id, blogData) => {
    if (!canEdit()) {
      return { success: false, error: 'You do not have permission to edit blogs' };
    }
    
    try {
      const response = await fetch(`${API_URL}/admin/blogs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(blogData),
      });
      
      const data = await response.json();
      if (response.ok) {
        setBlogs(prev => prev.map(blog => 
          blog._id === id ? data.blog : blog
        ));
        return { success: true, blog: data.blog };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const deleteBlog = async (id) => {
    if (!canDelete()) {
      return { success: false, error: 'You do not have permission to delete blogs' };
    }
    
    try {
      const response = await fetch(`${API_URL}/admin/blogs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setBlogs(prev => prev.filter(blog => blog._id !== id));
        return { success: true };
      } else {
        const data = await response.json();
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const getBlogById = (id) => {
    return blogs.find(blog => blog._id === id);
  };

  return (
    <BlogContext.Provider value={{
      blogs,
      loading,
      addBlog,
      updateBlog,
      deleteBlog,
      getBlogById,
      refreshBlogs: fetchBlogs
    }}>
      {children}
    </BlogContext.Provider>
  );
};